/**
 * 微信服务
 * 处理微信公众号相关的API调用
 */

export interface WechatUserInfoRequest {
    code: string;
}

export interface WechatUserInfoResponse {
    success: boolean;
    message: string;
    openid?: string;
    accessToken?: string;
    refreshToken?: string;
    expiresIn?: number;
    scope?: string;
}

class WechatService {
    private readonly API_BASE_URL = '/api/we-chat';

    /**
     * 通过授权码获取用户信息
     * @param code 微信授权码
     * @returns 用户信息响应
     */
    async getUserInfo(code: string): Promise<WechatUserInfoResponse> {
        try {
            const url = `${this.API_BASE_URL}/get-user-info`;
            console.log('调用微信用户信息接口，code:', code);
            console.log('请求URL:', url);
            console.log('完整请求URL:', window.location.origin + url);

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ code })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`请求失败: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            console.log('微信用户信息接口响应:', result);

            return result;
        } catch (error) {
            console.error('获取微信用户信息失败:', error);
            return {
                success: false,
                message: error instanceof Error ? error.message : '网络错误，请检查网络连接后重试'
            };
        }
    }

    /**
     * 检查是否在微信浏览器中
     */
    isWechatBrowser(): boolean {
        const ua = navigator.userAgent.toLowerCase();
        return ua.includes('micromessenger');
    }

    /**
     * 从URL中提取授权码
     */
    getCodeFromUrl(): string | null {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('code');
    }

    /**
     * 从URL中提取state参数
     */
    getStateFromUrl(): string | null {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('state');
    }

    /**
     * 检查URL中是否有微信授权错误
     */
    getErrorFromUrl(): { error: string | null; errorDescription: string | null } {
        const urlParams = new URLSearchParams(window.location.search);
        return {
            error: urlParams.get('error'),
            errorDescription: urlParams.get('error_description')
        };
    }

    /**
     * 生成微信授权URL
     * @param appId 微信公众号AppID
     * @param redirectUri 回调地址
     * @param scope 授权作用域
     * @param state 状态参数
     */
    generateAuthUrl(appId: string, redirectUri: string, scope: 'snsapi_base' | 'snsapi_userinfo' = 'snsapi_base', state: string = 'STATE'): string {
        const params = new URLSearchParams({
            appid: appId,
            redirect_uri: encodeURIComponent(redirectUri),
            response_type: 'code',
            scope: scope,
            state: state
        });

        return `https://open.weixin.qq.com/connect/oauth2/authorize?${params.toString()}#wechat_redirect`;
    }

    /**
     * 清理URL中的授权参数
     */
    cleanUrlParams(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        url.searchParams.delete('state');
        url.searchParams.delete('error');
        url.searchParams.delete('error_description');

        // 使用replace避免在浏览器历史中留下记录
        window.history.replaceState({}, document.title, url.toString());
    }
}

// 导出单例实例
export const wechatService = new WechatService();

// 导出类型和服务实例
export default wechatService;
